// BL-2437 (ADR 066 — Offline Mode § 3): the seller picker's search, answered from the synced
// sellers. It mirrors `GET /api/customers/search?include=seller` (BL-1478's five arms, ordered by
// account name, a term under two characters browses), so the picker reads the same offline.
import type { CustomerSearchResultDto } from '@auctogon/core-admin/schemas/responses/customer-search';

import { normalizeListSearchTerm } from '../lists/list-query-params';

/** The server's caps: 20 matches, 50 when browsing (`customer-picker-search.ts`). */
const MATCH_LIMIT = 20;
const BROWSE_LIMIT = 50;

/** Every text the server's predicate matches (`buildCustomerSearchPredicate`). */
function searchableTexts(seller: CustomerSearchResultDto): Array<string | null> {
  return [
    seller.identity.accountName,
    seller.customerNumber,
    seller.email,
    seller.identity.companyName,
    seller.identity.fullName,
  ];
}

function byAccountName(a: CustomerSearchResultDto, b: CustomerSearchResultDto): number {
  const left = a.identity.accountName ?? '';
  const right = b.identity.accountName ?? '';
  return left.localeCompare(right) || a.id.localeCompare(b.id);
}

/**
 * The synced sellers in the route's order (account name, then id). A caller that searches the same
 * list on every keystroke sorts it once with this and passes `presorted: true`.
 *
 * @param sellers - The cached seller rows.
 * @returns A sorted copy.
 */
export function sortOfflineSellers(sellers: readonly CustomerSearchResultDto[]): CustomerSearchResultDto[] {
  return [...sellers].sort(byAccountName);
}

/**
 * Search the synced sellers.
 *
 * @param input.sellers - The cached seller rows (`OfflineReferenceDto.sellers`).
 * @param input.search - The raw `search` query value.
 * @param input.presorted - The list is already in {@link sortOfflineSellers} order.
 * @returns The matching rows, as the online route orders and caps them.
 */
export function matchOfflineSellers(input: {
  sellers: readonly CustomerSearchResultDto[];
  search: string | null;
  presorted?: boolean;
}): CustomerSearchResultDto[] {
  const term = normalizeListSearchTerm(input.search) ?? '';
  const sorted = input.presorted ? input.sellers : sortOfflineSellers(input.sellers);
  if (term.length < 2) return sorted.slice(0, BROWSE_LIMIT);
  const needle = term.toLowerCase();
  return sorted
    .filter((seller) => searchableTexts(seller).some((text) => text != null && text.toLowerCase().includes(needle)))
    .slice(0, MATCH_LIMIT);
}
